<script setup lang="ts">
/* eslint-disable vue/no-undef-components */
import LegalMarkdownBody from '@/components/legal/LegalMarkdownBody.vue';
import { t } from '@/i18n';
import {
  absoluteLocalizedUrl,
  buildPathHreflangLinks,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';

const requestURL = useRequestURL();
const route = useRoute();
const siteLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const canonicalUrl = computed(() => absoluteLocalizedUrl(siteLocale.value, '/cookie-policy'));
const ogImageUrl = computed(() => `${requestURL.origin}/Veeso_og.jpg`);

useHead(() => ({
  title: t('landingPages.legal.cookie.seo.title'),
  htmlAttrs: { lang: siteLocale.value },
  meta: [
    { charset: 'utf-8' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { name: 'robots', content: 'index, follow' },
    { name: 'description', content: t('landingPages.legal.cookie.seo.description') },
    { property: 'og:title', content: t('landingPages.legal.cookie.seo.title') },
    { property: 'og:description', content: t('landingPages.legal.cookie.seo.description') },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:locale', content: ogLocaleFor(siteLocale.value) },
    { property: 'og:image', content: ogImageUrl.value },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: t('landingPages.legal.cookie.seo.title') },
    { name: 'twitter:image', content: ogImageUrl.value },
  ],
  link: [
    { rel: 'canonical', href: canonicalUrl.value },
    ...buildPathHreflangLinks('/cookie-policy'),
  ],
}));
</script>

<template>
  <div class="min-h-screen relative flex flex-col bg-white">
    <AppNav />

    <main class="relative z-[1] pt-[72px]">
      <div class="max-w-[640px] mx-auto px-4 md:px-8 py-16">
        <div class="w-full text-left mb-16">
          <h1
            class="text-[clamp(32px,8vw,48px)] font-bold tracking-tight text-black/90 mb-4 tracking-tight"
          >
            {{ t('landingPages.legal.cookie.page.title') }}
          </h1>
          <p class="text-sm font-normal text-black/40">
            {{ t('landingPages.legal.cookie.page.last_updated') }}
          </p>
        </div>

        <LegalMarkdownBody page-id="cookie-policy" />
      </div>
    </main>
    <AppFooter />
  </div>
</template>

<style scoped>
:deep(.legal-prose > :first-child) {
  margin-top: 0;
}
</style>
